'use client';

import { createContext, useContext, useEffect, useState } from 'react';

// Define the context type
type ThemeContextType = {
  primaryColor: string | null;
  setPrimaryColor: (color: string) => void;
};

// Create the context with a default throw for safety
const ThemeContext = createContext<ThemeContextType>({
  primaryColor: null,
  setPrimaryColor: () => {
    throw new Error('setPrimaryColor called outside ThemeProvider');
  },
});

// Custom hook
export const useThemeContext = () => useContext(ThemeContext);

// Provider component
export function ThemeProvider({
  children,
  primaryColor: propPrimaryColor,
  ...props
}: any) {
  const [primaryColor, setPrimaryColor] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedColor = localStorage.getItem('primaryColor');
    if (propPrimaryColor) {
      setPrimaryColor(propPrimaryColor);
      localStorage.setItem('primaryColor', propPrimaryColor);
      applyCustomColor(propPrimaryColor);
    } else if (savedColor) {
      setPrimaryColor(savedColor);
      applyCustomColor(savedColor);
    } else {
      // Set default color if no color is saved
      const defaultColor = 'hsl(24.6 95% 53.1%)';
      setPrimaryColor(defaultColor);
      localStorage.setItem('primaryColor', defaultColor);
      applyCustomColor(defaultColor);
    }
  }, [propPrimaryColor]);

  useEffect(() => {
    if (primaryColor) {
      applyCustomColor(primaryColor);
    }
  }, [primaryColor]);

  const applyCustomColor = (color: string) => {
    // Apply as CSS variables
    const root = document.documentElement;
    root.style.setProperty('--primary', color);
    root.style.setProperty('--primary-foreground', 'hsl(0 0% 98%)');
  };

  const handleSetPrimaryColor = (color: string) => {
    setPrimaryColor(color);
    localStorage.setItem('primaryColor', color);
    applyCustomColor(color);
  };

  if (!mounted) return null;

  return (
    <ThemeContext.Provider
      value={{
        primaryColor,
        setPrimaryColor: handleSetPrimaryColor,
      }}
    >
      <div className={props.className}>{children}</div>
    </ThemeContext.Provider>
  );
}
